import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MailPlus, Check, X, Bell, XCircle } from 'lucide-react';
import { invitationsAPI, carpoolsAPI } from '../services/api';
import Modal from '../components/Modal';
import { PageLoading, PageHead, PageEmpty, PagePanel, ActivityIconBadge } from '../components/appPage';
import { getActivityLabel } from '../utils/carpoolUi';
import './Invitations.css';

const Invitations = () => {
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState([]);
  const [carpools, setCarpools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    carpoolId: '',
    receiverEmail: '',
    receiverPhone: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invitationsRes, carpoolsRes] = await Promise.all([
        invitationsAPI.getInvitations(),
        carpoolsAPI.getCarpools()
      ]);
      setInvitations(invitationsRes.data.invitations);
      setCarpools(carpoolsRes.data.carpools);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    
    if (!formData.carpoolId) {
      setErrorMessage('Please select a carpool');
      setShowErrorModal(true);
      return;
    }

    if (!formData.receiverEmail && !formData.receiverPhone) {
      setErrorMessage('Please provide either email or phone number');
      setShowErrorModal(true);
      return;
    }

    try {
      await invitationsAPI.sendInvitation({
        carpoolId: formData.carpoolId,
        receiverEmail: formData.receiverEmail || undefined,
        receiverPhone: formData.receiverPhone || undefined
      });
      
      setShowSendModal(false);
      setShowSuccessModal(true);
      setSuccessMessage(t('invitations.sendSuccess'));
      setFormData({ carpoolId: '', receiverEmail: '', receiverPhone: '' });
      loadData();
    } catch (error) {
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleAccept = async (invitationId) => {
    try {
      await invitationsAPI.acceptInvitation(invitationId);
      loadData();
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleDecline = async (invitationId) => {
    try {
      await invitationsAPI.declineInvitation(invitationId);
      loadData();
    } catch (error) {
      console.error('Failed to decline invitation:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleReminder = async (invitationId) => {
    try {
      await invitationsAPI.sendReminder(invitationId);
      setSuccessMessage(t('invitations.invitationReminderSent'));
      setShowSuccessModal(true);
      loadData();
    } catch (error) {
      console.error('Failed to send reminder:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await invitationsAPI.cancelInvitation(invitationId);
      setSuccessMessage(t('invitations.invitationCancelled'));
      setShowSuccessModal(true);
      loadData();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="invitations-page page-shell">
        <PageLoading message={t('common.loading')} />
      </div>
    );
  }

  // Separate sent and received invitations
  const sentInvitations = invitations.filter(inv => inv.type === 'sent');
  const receivedInvitations = invitations.filter(inv => inv.type === 'received');
  
  const pendingReceived = receivedInvitations.filter(inv => inv.status === 'pending');
  const pendingSent = sentInvitations.filter(inv => inv.status === 'pending');
  const otherInvitations = invitations.filter(inv => inv.status !== 'pending');

  return (
    <div className="invitations-page page-shell">
      <PageHead
        title={t('invitations.title')}
        lead={t('invitations.lead', 'Преглеждайте и изпращайте покани към други родители за вашите карпули.')}
        actions={
          <button type="button" className="page-btn page-btn--primary" onClick={() => setShowSendModal(true)}>
            <MailPlus size={18} strokeWidth={2} aria-hidden />
            {t('invitations.sendInvitation')}
          </button>
        }
      />

      {/* Received Pending Invitations */}
      {pendingReceived.length > 0 && (
        <PagePanel
          className="invitations-block"
          title={`${t('invitations.pending')} — ${t('invitations.received')}`}
          titleId="inv-received-pending-heading"
        >
          <ul className="invitations-register">
            {pendingReceived.map((invitation) => (
              <li key={invitation.id} className="invitation-item">
                <div className="invitation-item__row">
                  <ActivityIconBadge activity={invitation.carpool.activity} size={22} />
                  <div className="invitation-item__body">
                    <span className="invitation-item__title">{invitation.carpool.name}</span>
                    <span className="invitation-item__meta">
                      {getActivityLabel(invitation.carpool.activity, t)} · {t('invitations.sentBy')}:{' '}
                      {invitation.sender.firstName} {invitation.sender.lastName}
                    </span>
                  </div>
                  <span className="status-badge status-pending">{invitation.status}</span>
                </div>
                <div className="invitation-item__actions invitation-item__actions--stack">
                  <button
                    type="button"
                    onClick={() => handleAccept(invitation.id)}
                    className="btn-success invitation-item__action-btn"
                  >
                    <Check size={18} strokeWidth={2} aria-hidden />
                    <span className="invitation-item__action-text">{t('invitations.accept')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDecline(invitation.id)}
                    className="btn-secondary invitation-item__action-btn"
                  >
                    <X size={18} strokeWidth={2} aria-hidden />
                    <span className="invitation-item__action-text">{t('invitations.decline')}</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </PagePanel>
      )}

      {/* Sent Pending Invitations */}
      {pendingSent.length > 0 && (
        <PagePanel
          className="invitations-block"
          title={`${t('invitations.pending')} — ${t('invitations.sent')}`}
          titleId="inv-sent-pending-heading"
        >
          <ul className="invitations-register">
            {pendingSent.map((invitation) => (
              <li key={invitation.id} className="invitation-item">
                <div className="invitation-item__row">
                  <ActivityIconBadge activity={invitation.carpool.activity} size={22} />
                  <div className="invitation-item__body">
                    <span className="invitation-item__title">{invitation.carpool.name}</span>
                    <span className="invitation-item__meta">
                      {getActivityLabel(invitation.carpool.activity, t)} · {t('invitations.sentTo')}:{' '}
                      {invitation.receiverEmail || invitation.receiverPhone || '—'} ·{' '}
                      {t('invitations.sentAt')}: {new Date(invitation.sentAt).toLocaleDateString()}
                    </span>
                    {invitation.reminderCount > 0 && (
                      <span className="invitation-item__hint">
                        {t('invitations.reminderCount', { count: invitation.reminderCount })}
                        {invitation.lastReminderSentAt &&
                          ` · ${new Date(invitation.lastReminderSentAt).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>
                  <span className="status-badge status-pending">{invitation.status}</span>
                </div>
                <div className="invitation-item__actions invitation-item__actions--stack">
                  <button
                    type="button"
                    onClick={() => handleReminder(invitation.id)}
                    className="btn-primary invitation-item__action-btn"
                  >
                    <Bell size={18} strokeWidth={2} aria-hidden />
                    <span className="invitation-item__action-text">{t('invitations.sendReminder')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="btn-danger-outline invitation-item__action-btn"
                  >
                    <XCircle size={18} strokeWidth={2} aria-hidden />
                    <span className="invitation-item__action-text">{t('common.cancel')}</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </PagePanel>
      )}

      {otherInvitations.length > 0 && (
        <PagePanel
          className="invitations-block"
          title={
            otherInvitations[0].status === 'accepted'
              ? t('invitations.accepted')
              : t('invitations.declined')
          }
          titleId="inv-other-heading"
        >
          <ul className="invitations-register">
            {otherInvitations.map((invitation) => (
              <li key={invitation.id} className="invitation-item">
                <div className="invitation-item__row">
                  <ActivityIconBadge activity={invitation.carpool.activity} size={22} />
                  <div className="invitation-item__body">
                    <span className="invitation-item__title">{invitation.carpool.name}</span>
                    <span className="invitation-item__meta">
                      {getActivityLabel(invitation.carpool.activity, t)} · {t('invitations.sentBy')}:{' '}
                      {invitation.sender.firstName} {invitation.sender.lastName}
                    </span>
                  </div>
                  <span className={`status-badge status-${invitation.status}`}>{invitation.status}</span>
                </div>
              </li>
            ))}
          </ul>
        </PagePanel>
      )}

      {invitations.length === 0 && (
        <PageEmpty
          title={t('invitations.noInvitations')}
          text={t('invitations.emptyLead', 'Изпратете покана от страница на карпул или от бутона по-горе.')}
        />
      )}

      {/* Send Invitation Modal */}
      <Modal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        title={t('invitations.sendInvitation')}
        size="medium"
      >
        <form onSubmit={handleSendInvitation} className="invitation-form">
          <div className="form-group">
            <label>{t('carpools.title')}</label>
            <select
              value={formData.carpoolId}
              onChange={(e) => setFormData({...formData, carpoolId: e.target.value})}
              className="form-input"
              required
            >
              <option value="">{t('common.select') || 'Select a carpool'}</option>
              {carpools.map(carpool => (
                <option key={carpool.id} value={carpool.id}>
                  {carpool.name} — {getActivityLabel(carpool.activity, t)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('auth.email')}</label>
            <input
              type="email"
              value={formData.receiverEmail}
              onChange={(e) => setFormData({...formData, receiverEmail: e.target.value})}
              className="form-input"
              placeholder="parent@example.com"
            />
          </div>

          <div className="form-group">
            <label>{t('auth.phone')}</label>
            <input
              type="tel"
              value={formData.receiverPhone}
              onChange={(e) => setFormData({...formData, receiverPhone: e.target.value})}
              className="form-input"
              placeholder="+359..."
            />
          </div>

          <p className="form-help">
            {t('invitations.sendInvitationHelp') || 'Provide either email or phone number'}
          </p>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowSendModal(false)}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary">
              {t('invitations.sendInvitation')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={t('common.success')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <p className="confirm-modal-description">{successMessage || t('common.success')}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowSuccessModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={t('errors.error')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <p className="confirm-modal-description">{errorMessage}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowErrorModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Invitations;
